import { LscApiError } from './api';
import type { LscAdminHealth, LscAdminHealthKnownWarning, LscAdminHealthSafetyLocks } from './types';

export const LSC_ADMIN_HEALTH_TITLE = 'Production health';
export const LSC_ADMIN_HEALTH_INTRO =
  'Read-only operational summary for deploy identity, billing posture, automation bridge, smoke baseline, and admin release readiness.';
export const LSC_ADMIN_HEALTH_FRONTEND_NOTE =
  'Frontend bundle identity is not embedded in the app build. Verify the live bundle through deploy verification or smoke checks.';
export const LSC_ADMIN_HEALTH_SMOKE_NOTE =
  'Accepted smoke baseline is 32 pass / 1 fail / 2 defer until bundle hygiene is addressed separately.';
export const LSC_ADMIN_HEALTH_READINESS_NOTE =
  'Readiness markers are doc-sourced reference values, not live product truth.';
export const LSC_ADMIN_HEALTH_NO_MUTATION_NOTE =
  'This panel does not change billing, env, scheduler, Cloud Tasks, Stripe, Firestore, or archive state.';

export const LSC_ADMIN_KNOWN_WARNING_BUNDLE_HYGIENE_TITLE =
  'Bundle hygiene: internal production env prefix substring in frontend bundle';
export const LSC_ADMIN_KNOWN_WARNING_BUNDLE_HYGIENE_DETAIL =
  'Smoke detects an internal production env prefix marker from sanitizer regex source in the customer bundle. This is not customer-facing copy and does not block support triage.';

const INTERNAL_COPY_PATTERN =
  /firestore|cloud run|webhook payload|oauth|api_key|secret|token|session cookie|allowlist uid|cus_|sub_|sk_live|sk_test/i;

export function parseAdminHealthError(err: unknown, fallback?: string): string {
  if (err instanceof LscApiError) {
    if (err.status === 403 || err.code === 'admin_forbidden') {
      return 'You do not have access to LSC admin tools.';
    }
    if (err.code === 'admin_health_unavailable') {
      return 'Production health is temporarily unavailable.';
    }
    if (err.message && !INTERNAL_COPY_PATTERN.test(err.message)) {
      return err.message;
    }
  }
  if (err instanceof Error && err.message && !INTERNAL_COPY_PATTERN.test(err.message)) {
    return err.message;
  }
  return fallback ?? 'Unable to load production health.';
}

export function formatAdminHealthBoolean(value: boolean | undefined | null): string {
  if (value === true) return 'On';
  if (value === false) return 'Off';
  return 'Unknown';
}

export function formatAdminHealthValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return formatAdminHealthBoolean(value);
  return String(value);
}

export function adminHealthSmokeBaselineLabel(baseline?: string | null): string {
  const value = String(baseline || '').trim();
  if (!value) return 'Unknown';
  if (value === '32_pass_1_fail_2_defer') return '32 pass / 1 fail / 2 defer';
  return value.replace(/_/g, ' ');
}

export function adminHealthWarningLabel(warning: LscAdminHealthKnownWarning): string {
  if (warning.id === 'frontend-bundle-forbidden-lsc-production') {
    return LSC_ADMIN_KNOWN_WARNING_BUNDLE_HYGIENE_TITLE;
  }
  return warning.summary || warning.id || 'Known warning';
}

export function adminHealthWarningDetail(warning: LscAdminHealthKnownWarning): string {
  if (warning.id === 'frontend-bundle-forbidden-lsc-production') {
    return LSC_ADMIN_KNOWN_WARNING_BUNDLE_HYGIENE_DETAIL;
  }
  return warning.summary || 'Review deploy verification runbook.';
}

export function adminHealthReleaseItems(
  adminRelease: LscAdminHealth['admin_release'] | undefined
): Array<{ key: string; label: string; ready: boolean }> {
  return [
    { key: 'support_issue_backend', label: 'Support issue backend', ready: !!adminRelease?.support_issue_backend },
    { key: 'report_issue_ux', label: 'Report issue UX', ready: !!adminRelease?.report_issue_ux },
    { key: 'admin_issue_queue', label: 'Admin issue queue', ready: !!adminRelease?.admin_issue_queue },
    { key: 'league_health_summary', label: 'League health summary', ready: !!adminRelease?.league_health_summary },
    {
      key: 'production_health_widget',
      label: 'Production health widget',
      ready: !!adminRelease?.production_health_widget,
    },
  ];
}

export function adminHealthSafetyLockItems(
  safetyLocks: LscAdminHealthSafetyLocks | undefined
): Array<{ key: string; label: string; locked: boolean }> {
  return [
    { key: 'env_mutation_unavailable', label: 'Env mutation unavailable', locked: !!safetyLocks?.env_mutation_unavailable },
    {
      key: 'scheduler_controls_unavailable',
      label: 'Scheduler controls unavailable',
      locked: !!safetyLocks?.scheduler_controls_unavailable,
    },
    {
      key: 'cloud_tasks_controls_unavailable',
      label: 'Cloud Tasks controls unavailable',
      locked: !!safetyLocks?.cloud_tasks_controls_unavailable,
    },
    {
      key: 'stripe_mutation_unavailable',
      label: 'Stripe mutation unavailable',
      locked: !!safetyLocks?.stripe_mutation_unavailable,
    },
    {
      key: 'firestore_editor_unavailable',
      label: 'Firestore editor unavailable',
      locked: !!safetyLocks?.firestore_editor_unavailable,
    },
    {
      key: 'archive_autopublish_stage2_locked',
      label: 'Archive autopublish Stage 2 locked',
      locked: !!safetyLocks?.archive_autopublish_stage2_locked,
    },
    {
      key: 'controlled_apply_locked',
      label: 'Controlled apply locked',
      locked: !!safetyLocks?.controlled_apply_locked,
    },
  ];
}

export function assertAdminHealthCopySafe(text: string): boolean {
  return !INTERNAL_COPY_PATTERN.test(text);
}

export const ADMIN_HEALTH_FORBIDDEN_CONTROL_LABELS = [
  'Deploy now',
  'Change env',
  'Firestore editor',
  'Stripe subscription',
  'Scheduler trigger',
  'Cloud Tasks trigger',
  'Force schedule apply',
  'VOD backfill',
  'Ownership transfer',
  'Impersonate',
] as const;
